import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CadsBadgeBitConfig } from './cads-badge-bit.config';

/**
 * Pill badge bit: optional status dot or Material Symbols icon plus label. Semantic `color` uses the same ramp as info badge:
 * pastel surface, base border and dot, deep label and icon glyph ({@link CadsBadgeBitConfig}; `input()`; no i18n inside CADS v1).
 */
@Component({
    selector: 'cads-badge-bit',
    imports: [NgStyle],
    templateUrl: './cads-badge-bit.component.html',
    styleUrl: './cads-badge-bit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsBadgeBitComponent {
  /** Badge options (label, semantic color, optional icon ligature, size). */
  readonly config = input.required<CadsBadgeBitConfig>();

  /** Resolved config with defaults (`color` **mid-gray**, `size` **small**). Exposed for the template. */
  readonly effectiveConfig = computed(() => {
    const c = this.config();
    return {
      label: c.label,
      color: c.color ?? 'mid-gray',
      icon: c.icon,
      size: c.size ?? 'small',
    };
  });

  /** CSS custom properties mapping the semantic key to base, deep, and pastel palette tokens. */
  readonly cssVars = computed<Record<string, string>>(() => {
    const key = this.effectiveConfig().color;
    return {
      '--cads-badge-bit-base': `var(--cads-color-${key})`,
      '--cads-badge-bit-deep': `var(--cads-color-${key}-deep)`,
      '--cads-badge-bit-pastel': `var(--cads-color-${key}-pastel)`,
    };
  });

  readonly isXs = computed(() => this.effectiveConfig().size === 'xs');
  readonly icon = computed(() => this.effectiveConfig().icon);
}
